  (function () {
    if (!window.customElements) return;

    /**
     * Non-modal desktop search panel. The shell (exclusivity, the shared page
     * overlay and the search:* events) is owned by
     * themeUtils.surfaces; this element registers as the 'desktop-search'
     * surface and owns only the Athos search engine (ported verbatim) and its
     * own data-state. Escape / overlay-click closes route through the manager.
     */
    class DesktopSearchPanel extends HTMLElement {
      /** Relocate to <body> once, de-dupe stale instances, then initialise. */
      connectedCallback() {
        /* A fixed, viewport-relative panel cannot live inside the header's
           `will-change: transform` containing block, so relocate to <body>.
           De-dupe first so theme-editor re-renders never strand a second
           panel (parity row 14 / audit B8). */
        if (this.parentElement && this.parentElement !== document.body) {
          document.querySelectorAll('desktop-search-panel').forEach((el) => {
            if (el !== this && el.parentElement === document.body) el.remove();
          });
          document.body.appendChild(this);
          return; /* the reconnection runs the init branch below */
        }
        if (this._initialised) return;
        this._initialised = true;
        this.#init();
      }

      /** Tear down on genuine removal (ignore the transient relocation move). */
      disconnectedCallback() {
        if (this._initialised) this.#teardown();
      }

      /**
       * Release listeners, unregister the surface and remove the injected
       * clear button so a re-render leaves no duplicates behind.
       * @returns {void}
       */
      #teardown() {
        this._initialised = false;
        if (this._unregister) {
          this._unregister();
          this._unregister = null;
        }
        if (this._controller) {
          this._controller.abort();
          this._controller = null;
        }
        if (this._clearBtn) {
          this._clearBtn.remove();
          this._clearBtn = null;
        }
      }

      /**
       * Build the Athos search engine, register the surface and wire events.
       * The engine functions are ported verbatim from the previous shell;
       * only state classes (→ data attributes) and the drawer chrome differ.
       * @returns {void}
       */
      #init() {
        const host = this;
        const utils = window.themeUtils;
        if (!utils) return;

        const debounce = utils.debounce;
        const surfaces = utils.surfaces;

        this._controller = new AbortController();
        const signal = this._controller.signal;

        /* ── Configuration ──────────────────────────── */

        const MIN_QUERY_LENGTH = 3;
        const SEARCH_SPRING_TAG = host.dataset.athosId;
        const MONEY_FORMAT = host.dataset.moneyFormat;
        const I18N_NO_TOP_SEARCHES = host.dataset.i18nNoTopSearches;

        /* ── DOM Cache ──────────────────────────────── */

        const header = document.querySelector('.header');
        const desktopInput = header && header.querySelector('[data-desktop-search] input');
        const topList = host.querySelector('[data-search-top-list]');
        const recentList = host.querySelector('[data-search-recent-list]');
        const recentEmpty = host.querySelector('[data-search-recent-empty]');
        const suggestionsCol = host.querySelector('[data-search-col="suggestions"]');
        const suggestionsList = host.querySelector('[data-search-suggestions-list]');
        const productsGrid = host.querySelector('[data-search-products]');
        const noResultsEl = host.querySelector('[data-search-no-results]');
        const loader = host.querySelector('[data-search-loader]');
        const viewAllBtn = host.querySelector('[data-search-view-all]');
        const totalCountEl = host.querySelector('[data-search-total-count]');
        const termTemplate = host.querySelector('[data-search-term-template]');
        const productTemplate = host.querySelector('[data-search-product-template]');
        const skeletonTemplate = host.querySelector('[data-search-skeleton-template]');
        const emptyTemplate = host.querySelector('[data-search-empty-template]');
        const clearIconTemplate = host.querySelector('[data-js="desktop-search-clear-icon"]');

        /* ── State ──────────────────────────────────── */

        let isActive = false;
        let activeAbortController = null;
        let activeObserver = null;
        let defaultProducts = null;
        let pendingRedirect = null;

        /* ── Position Tracking ──────────────────────── */

        /**
         * Pin the panel directly beneath the header's current bottom edge.
         * @returns {void}
         */
        function updatePosition() {
          if (!header) return;
          const bottom = header.getBoundingClientRect().bottom;
          host.style.setProperty('--desktop-search-top', bottom + 'px');
        }

        document.addEventListener('header:sticky-change', updatePosition, { signal });

        /* ── Clear Button ───────────────────────────── */

        /**
         * Inject the clear button into the header search field once.
         * @returns {void}
         */
        function ensureClearBtn() {
          if (host._clearBtn || !desktopInput || !clearIconTemplate) return;
          const placeholder = desktopInput.closest('.desktop-search');
          if (!placeholder) return;

          const clearBtn = document.createElement('button');
          clearBtn.type = 'button';
          clearBtn.className = 'desktop-search__clear';
          clearBtn.replaceChildren(clearIconTemplate.content.cloneNode(true));
          placeholder.appendChild(clearBtn);
          host._clearBtn = clearBtn;

          clearBtn.addEventListener('click', function () {
            desktopInput.value = '';
            clearBtn.removeAttribute('data-visible');
            handleQueryChange('');
            desktopInput.focus();
          });
        }

        /**
         * Sync the clear button's visibility with the input's contents.
         * @returns {void}
         */
        function updateClearBtn() {
          if (!host._clearBtn) return;
          host._clearBtn.toggleAttribute('data-visible', desktopInput && desktopInput.value.length > 0);
        }

        /* ── History Management ─────────────────────── */

        /**
         * Render the recent-search list from local Athos history.
         * @returns {void}
         */
        function renderRecent() {
          const history = window.AthosAPI.getHistory();

          if (history.length === 0) {
            recentList.replaceChildren();
            recentList.appendChild(recentEmpty.cloneNode(true));
            return;
          }

          const frag = document.createDocumentFragment();
          history.forEach(function (item) {
            const term = typeof item === 'string' ? item : item.searchQuery || item.query || '';
            if (!term) return;
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });

          recentList.replaceChildren();
          recentList.appendChild(frag);
        }

        /* ── Trending (Top Searches) ────────────────── */

        /**
         * Fetch the trending search terms from Athos.
         * @returns {Promise<string[]>}
         */
        async function fetchTrending() {
          return window.AthosAPI.fetchTrending();
        }

        /**
         * Render the trending (top searches) list.
         * @param {string[]} terms
         * @returns {void}
         */
        function renderTrending(terms) {
          topList.replaceChildren();

          if (terms.length === 0) {
            const li = document.createElement('li');
            li.textContent = I18N_NO_TOP_SEARCHES;
            topList.appendChild(li);
            return;
          }

          const frag = document.createDocumentFragment();
          terms.forEach(function (term) {
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });

          topList.appendChild(frag);
        }

        /* ── Default Recommended Products ───────────── */

        /**
         * Fetch the default recommended products for the configured profile.
         * @returns {Promise<Object|null>}
         */
        async function fetchDefaultProducts() {
          if (!SEARCH_SPRING_TAG) return null;
          const profiles = await window.AthosAPI.fetchRecommend({ profiles: [SEARCH_SPRING_TAG] });
          const profile = profiles && profiles[0] ? profiles[0] : null;
          if (!profile || !profile.results) return null;
          profile.results = profile.results.slice(0, 4).map(window.AthosAPI.normaliseProductResult);
          await primePresentmentPrices(profile.results);
          return profile;
        }

        /**
         * Prime the Storefront presentment-price cache for a product set so
         * renderProducts can overlay exact market prices synchronously.
         * No-op for base-currency shoppers.
         * @param {Object[]} products
         * @returns {Promise<void>}
         */
        async function primePresentmentPrices(products) {
          if (!window.AthosAPI || typeof window.AthosAPI.fetchPresentmentPrices !== 'function') return;
          const handles = (products || [])
            .map(function (p) {
              return p.handle || '';
            })
            .filter(Boolean);
          if (handles.length === 0) return;
          await window.AthosAPI.fetchPresentmentPrices({ handles });
        }

        /* ── Search Query (Suggestions + Products) ──── */

        /**
         * Fetch autocomplete suggestions + products for a query.
         * @param {string} query
         * @returns {Promise<Object>}
         */
        async function fetchSuggestions(query) {
          if (activeAbortController) activeAbortController.abort();
          activeAbortController = new AbortController();

          const result = await window.AthosAPI.fetchSuggest({
            query,
            limit: 4,
            signal: activeAbortController.signal,
          });
          if (result && result.ok) await primePresentmentPrices(result.products);
          return result;
        }

        /* ── Render Products ────────────────────────── */

        /**
         * Render the product-card skeletons while results load.
         * @returns {void}
         */
        function showProductSkeletons() {
          productsGrid.replaceChildren();

          for (let i = 0; i < 4; i++) {
            productsGrid.appendChild(skeletonTemplate.content.cloneNode(true));
          }
        }

        /**
         * Show the loading overlay.
         * @returns {void}
         */
        function showLoader() {
          loader.setAttribute('aria-hidden', 'false');
        }

        /**
         * Hide the loading overlay.
         * @returns {void}
         */
        function hideLoader() {
          loader.setAttribute('aria-hidden', 'true');
        }

        /**
         * Render a set of product cards, wiring beacon render/impression/click.
         * @param {Object[]} products
         * @param {boolean} isSearch - true for autocomplete, false for recommend.
         * @param {string|null} [responseId]
         * @param {string|null} [tag]
         * @returns {void}
         */
        function renderProducts(products, isSearch, responseId = null, tag = null) {
          if (activeObserver) {
            activeObserver.disconnect();
            activeObserver = null;
          }
          productsGrid.replaceChildren();
          hideLoader();

          if (!products || products.length === 0) {
            if (isSearch) {
              noResultsEl.setAttribute('aria-hidden', 'false');
            } else {
              productsGrid.appendChild(emptyTemplate.content.cloneNode(true));
            }
            return;
          }

          noResultsEl.setAttribute('aria-hidden', 'true');

          const frag = document.createDocumentFragment();
          products.forEach(function (product) {
            const name = product.name || product.title || '';
            const core = (product.mappings && product.mappings.core) || {};
            let priceValue = parseFloat(core.price ?? product.price ?? core.msrp ?? product.msrp ?? 0) || 0;
            let msrpValue = parseFloat(core.msrp ?? product.msrp ?? 0) || 0;
            const imageUrl = product.thumbnailImageUrl || product.imageUrl || product.image || '';
            const handle = product.handle || product.url || '';
            if (!handle) return;
            // Athos indexes base-currency (AUD) feed prices; market shoppers
            // get the exact presentment price primed from the Storefront API.
            const presentment =
              typeof window.AthosAPI.getPresentmentPrice === 'function'
                ? window.AthosAPI.getPresentmentPrice(product.handle || '')
                : null;
            if (presentment) {
              priceValue = presentment.price / 100;
              msrpValue = presentment.compareAt / 100;
            }
            const onSale = msrpValue > 0 && msrpValue > priceValue;
            const promo =
              window.PromoPricing && typeof window.PromoPricing.resolve === 'function'
                ? window.PromoPricing.resolve({
                    priceCents: Math.round(priceValue * 100),
                    tags: product.ss_tags,
                  })
                : null;
            const productUrl = handle.startsWith('/') ? handle : '/products/' + handle;

            const clone = productTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = productUrl;
            a.setAttribute('aria-label', name);

            const img = clone.querySelector('img');
            if (imageUrl) {
              if (window.AthosAPI && typeof window.AthosAPI.buildSrcset === 'function') {
                const srcset = window.AthosAPI.buildSrcset(imageUrl, [270, 360, 540]);
                if (srcset) img.setAttribute('srcset', srcset);
              }
              img.src = imageUrl;
              img.alt = name;
            } else {
              img.remove();
            }

            clone.querySelector('.desktop-search-panel__product-title').textContent = name;

            const priceEl = clone.querySelector('.desktop-search-panel__product-price');
            priceEl.replaceChildren();
            if (onSale) {
              const compareSpan = document.createElement('span');
              compareSpan.className = 'desktop-search-panel__price';
              compareSpan.dataset.type = 'sale';
              compareSpan.textContent = window.AthosAPI.formatMoney(msrpValue, MONEY_FORMAT, false);
              const saleSpan = document.createElement('span');
              saleSpan.className = 'desktop-search-panel__price';
              saleSpan.dataset.type = 'discount';
              saleSpan.textContent = window.AthosAPI.formatMoney(priceValue, MONEY_FORMAT, false);
              priceEl.append(compareSpan, saleSpan);
            } else {
              const span = document.createElement('span');
              span.className = 'desktop-search-panel__price';
              span.textContent = window.AthosAPI.formatMoney(priceValue, MONEY_FORMAT, false);
              priceEl.append(span);
            }

            if (promo) {
              const promoText = window.PromoPricing.message(
                window.AthosAPI.formatMoney(promo.priceCents, MONEY_FORMAT, true),
                promo.percent,
              );
              if (promoText) {
                const promoSpan = document.createElement('span');
                promoSpan.className = 'desktop-search-panel__price';
                promoSpan.dataset.type = 'promo';
                promoSpan.textContent = promoText;
                priceEl.append(promoSpan);
              }
            }

            if (responseId && window.AthosBeacon) {
              a.addEventListener('click', function (e) {
                if (!e.target.closest('a[href]')) return;
                if (isSearch) {
                  if (typeof window.AthosBeacon.sendAutocompleteClickthrough === 'function') {
                    window.AthosBeacon.sendAutocompleteClickthrough(responseId, product, false);
                  }
                } else if (tag) {
                  if (typeof window.AthosBeacon.sendRecommendClickthrough === 'function') {
                    window.AthosBeacon.sendRecommendClickthrough(tag, responseId, product);
                  }
                }
              });
            }

            frag.appendChild(a);
          });

          productsGrid.appendChild(frag);

          const cards = Array.from(productsGrid.children);
          if (window.AthosBeacon && responseId) {
            if (!isSearch && tag) {
              window.AthosBeacon.sendRecommendRender(tag, responseId);
            }

            if ('IntersectionObserver' in window) {
              activeObserver = new IntersectionObserver(
                function (entries) {
                  entries.forEach(function (entry) {
                    if (!entry.isIntersecting) return;
                    const index = cards.indexOf(entry.target);
                    if (index === -1) return;
                    activeObserver.unobserve(entry.target);
                    if (products[index]) {
                      if (isSearch) {
                        window.AthosBeacon.sendAutocompleteImpression(responseId, [products[index]], []);
                      } else if (tag) {
                        window.AthosBeacon.sendRecommendImpression(tag, responseId, [products[index]]);
                      }
                    }
                  });
                },
                { threshold: 0.5 }
              );

              cards.forEach(function (card) {
                activeObserver.observe(card);
              });
            } else {
              if (isSearch) {
                window.AthosBeacon.sendAutocompleteImpression(responseId, products, []);
              } else if (tag) {
                window.AthosBeacon.sendRecommendImpression(tag, responseId, products);
              }
            }
          }

          /* Stagger visibility for animation */
          requestAnimationFrame(function () {
            const items = productsGrid.querySelectorAll('.desktop-search-panel__product');
            items.forEach(function (item) {
              item.setAttribute('data-visible', 'true');
            });
          });
        }

        /* ── Render Suggestions ─────────────────────── */

        /**
         * Render the autocomplete suggestion terms.
         * @param {string[]} terms
         * @returns {void}
         */
        function renderSuggestions(terms) {
          suggestionsList.replaceChildren();

          if (terms.length === 0) {
            suggestionsCol.setAttribute('aria-hidden', 'true');
            return;
          }

          suggestionsCol.setAttribute('aria-hidden', 'false');
          const frag = document.createDocumentFragment();

          terms.forEach(function (term) {
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });

          suggestionsList.appendChild(frag);
        }

        /* ── Query Change Handler ───────────────────── */

        const handleQueryChange = debounce(async function (query) {
          query = (query || '').trim();

          if (query.length < MIN_QUERY_LENGTH) {
            if (activeAbortController) {
              activeAbortController.abort();
              activeAbortController = null;
            }
            pendingRedirect = null;
            /* Reset to default state */
            suggestionsCol.setAttribute('aria-hidden', 'true');
            suggestionsList.replaceChildren();
            viewAllBtn.setAttribute('aria-hidden', 'true');
            noResultsEl.setAttribute('aria-hidden', 'true');

            /* Show default products */
            if (defaultProducts && defaultProducts.results && defaultProducts.results.length > 0) {
              renderProducts(defaultProducts.results, false, defaultProducts.responseId, defaultProducts.tag);
            } else {
              showProductSkeletons();
            }
            return;
          }

          /* Show loader on products */
          showLoader();

          const result = await fetchSuggestions(query);
          if (result.aborted) {
            hideLoader();
            return;
          }
          if (result.error) {
            hideLoader();
            noResultsEl.setAttribute('aria-hidden', 'true');
            return;
          }

          renderSuggestions(result.suggestions);
          noResultsEl.setAttribute('aria-hidden', 'true');
          renderProducts(result.products, true, result.responseId);

          const safeRedirect = window.AthosAPI.sanitiseRedirect(result.redirect);
          if (safeRedirect) {
            pendingRedirect = { query: query, redirect: safeRedirect, responseId: result.responseId || '' };
          } else {
            pendingRedirect = null;
          }

          if (result.responseId && window.AthosBeacon) {
            if (typeof window.AthosBeacon.sendAutocompleteRender === 'function') {
              window.AthosBeacon.sendAutocompleteRender(result.responseId);
            }

            if (
              result.banners &&
              result.banners.length > 0 &&
              typeof window.AthosBeacon.sendAutocompleteImpression === 'function'
            ) {
              window.AthosBeacon.sendAutocompleteImpression(result.responseId, [], result.banners);
            }
          }

          if (result.totalResults > 0) {
            totalCountEl.textContent = result.totalResults;
            viewAllBtn.href = '/search?q=' + encodeURIComponent(query);
            viewAllBtn.setAttribute('aria-hidden', 'false');
          } else {
            viewAllBtn.setAttribute('aria-hidden', 'true');
          }
        }, 300);

        /* ── Open / Close (driven by the surface manager) ── */

        /**
         * Open work — reveals the panel, renders recent, trending, default
         * products and (if the input is prefilled) the query results. Called
         * only by themeUtils.surfaces.
         * @param {Object} [detail] - Open detail (unused for desktop search).
         * @param {Object} [opts] - Open options (unused for desktop search).
         * @returns {Promise<void>}
         */
        async function openPanel(detail, opts) {
          if (isActive) return;
          isActive = true;

          if (desktopInput) desktopInput.setAttribute('aria-expanded', 'true');
          noResultsEl.setAttribute('aria-hidden', 'true');

          updatePosition();
          ensureClearBtn();
          updateClearBtn();

          /* Focus the input */
          if (desktopInput) {
            desktopInput.focus();
          }

          host.dataset.state = 'open';
          host.setAttribute('aria-hidden', 'false');

          /* Render recent searches */
          renderRecent();

          /* Reset suggestions column */
          suggestionsCol.setAttribute('aria-hidden', 'true');
          viewAllBtn.setAttribute('aria-hidden', 'true');

          /* Show product skeletons initially */
          showProductSkeletons();

          /* Fetch trending (top searches) */
          const terms = await fetchTrending();
          renderTrending(terms);

          /* If there's already a query, run it */
          if (desktopInput && desktopInput.value.trim().length >= MIN_QUERY_LENGTH) {
            handleQueryChange(desktopInput.value.trim());
          } else {
            /* Load default recommended products */
            if (!defaultProducts) {
              defaultProducts = await fetchDefaultProducts();
            }
            if (defaultProducts && defaultProducts.results && defaultProducts.results.length > 0) {
              renderProducts(defaultProducts.results, false, defaultProducts.responseId, defaultProducts.tag);
            } else {
              showProductSkeletons();
            }
          }
        }

        /**
         * Close work — hides the panel, clears the input and cancels in-flight
         * requests. The overlay, is-search-active header state and the
         * search:close event are handled by the manager/header.
         * @returns {void}
         */
        function closePanel() {
          if (!isActive) return;
          isActive = false;
          pendingRedirect = null;

          delete host.dataset.state;
          host.setAttribute('aria-hidden', 'true');

          if (desktopInput) desktopInput.setAttribute('aria-expanded', 'false');

          /* Clear input so the trending placeholder animation resumes */
          if (desktopInput) {
            desktopInput.value = '';
            if (host._clearBtn) host._clearBtn.removeAttribute('data-visible');
            desktopInput.blur();
          }

          /* Cancel a debounced query render still in flight so it cannot fire
             render/impression beacons after the panel has torn down. */
          if (handleQueryChange && typeof handleQueryChange.cancel === 'function') {
            handleQueryChange.cancel();
          }

          /* Disconnect the impression observer so no product-impression
             beacons fire against a torn-down responseId. A fresh render
             re-creates it. */
          if (activeObserver) {
            activeObserver.disconnect();
            activeObserver = null;
          }

          /* Cancel in-flight requests */
          if (activeAbortController) {
            activeAbortController.abort();
            activeAbortController = null;
          }
        }

        /* ── Surface Registration ───────────────────── */

        this._unregister = surfaces.register('desktop-search', {
          open: openPanel,
          close: closePanel,
          exclusive: true,
          overlay: true,
          search: true,
        });

        /* ── Input Events ───────────────────────────── */

        if (desktopInput) {
          desktopInput.addEventListener(
            'input',
            function () {
              const val = desktopInput.value.trim();
              updateClearBtn();
              handleQueryChange(val);
            },
            { signal }
          );

          desktopInput.addEventListener(
            'keydown',
            function (e) {
              if (e.key === 'Enter') {
                e.preventDefault();
                const q = desktopInput.value.trim();
                if (pendingRedirect && pendingRedirect.query === q) {
                  if (window.AthosBeacon && typeof window.AthosBeacon.sendAutocompleteRedirect === 'function') {
                    window.AthosBeacon.sendAutocompleteRedirect(pendingRedirect.responseId, pendingRedirect.redirect);
                  }
                  window.location.href = pendingRedirect.redirect;
                  return;
                }
                if (q.length > 0) {
                  window.AthosAPI.saveToHistory(q);
                  window.location.href = '/search?q=' + encodeURIComponent(q);
                }
              }

              if (e.key === 'Escape') {
                surfaces.close('desktop-search');
                desktopInput.blur();
              }
            },
            { signal }
          );
        }

        /* ── Close on Escape (global) ───────────────── */

        document.addEventListener(
          'keydown',
          function (e) {
            if (e.key === 'Escape' && isActive) {
              surfaces.close('desktop-search');
              if (desktopInput) desktopInput.blur();
            }
          },
          { signal }
        );

        /* ── View All click ─────────────────────────── */

        if (viewAllBtn) {
          viewAllBtn.addEventListener('click', function () {
            const q = desktopInput ? desktopInput.value.trim() : '';
            if (q.length > 0) {
              window.AthosAPI.saveToHistory(q);
            }
          });
        }

        /* ── Arrow Key Navigation ───────────────────── */

        host.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

          const list = document.activeElement && document.activeElement.closest('.desktop-search-panel__list');
          if (!list) return;

          const links = Array.from(list.querySelectorAll('a'));
          if (links.length === 0) return;

          const idx = links.indexOf(document.activeElement);
          if (idx === -1) return;

          e.preventDefault();
          let next;
          if (e.key === 'ArrowDown') {
            next = links[idx + 1] || links[0];
          } else {
            next = links[idx - 1] || links[links.length - 1];
          }
          next.focus();
        });
      }
    }

    if (!window.customElements.get('desktop-search-panel')) {
      window.customElements.define('desktop-search-panel', DesktopSearchPanel);
    }
  })();
